import React from "react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Chip, Stack, Tooltip } from "@mui/material";

import { EasyWinChip } from "../EasyWin/EasyWin";
import { DisplayStatus, type Insight, InsightPriority } from "../types";

const providerDisplayNames = {
  "aws-trusted-advisor": "Trusted Advisor",
  "aws-cost-optimization-hub": "Cost Optimization Hub",
  "gcp-recommender": "Google Cloud Recommender",
};

const getStatusBadge = (insight: Insight): React.ReactNode => {
  switch (insight.displayStatus) {
    case DisplayStatus.PERMISSIONS_NEEDED:
      return (
        <Chip
          variant="status"
          label="Permissions needed"
          color="warning"
          data-cy="permissions-needed-label"
          sx={{ ml: 1, mb: 0.3 }}
          icon={
            <Tooltip
              title={`Enable ${providerDisplayNames[insight.providerId] ?? "these"} insights on your accounts to get detailed recommendations, savings estimates and more.`}
              arrow
            >
              <InfoOutlinedIcon />
            </Tooltip>
          }
        />
      );
    case DisplayStatus.DISMISSED:
      return <Chip label="Dismissed" variant="status" size="small" sx={{ ml: 1, mb: 0.3 }} />;
    case DisplayStatus.OPTIMIZED:
      return <Chip label="Optimized" variant="status" size="small" color="success" sx={{ ml: 1, mb: 0.3 }} />;
    case DisplayStatus.ACTIONABLE:
      return <Chip label="Actionable" variant="status" size="small" color="primary" sx={{ ml: 1, mb: 0.3 }} />;
    case DisplayStatus.ACKNOWLEDGED:
      return <Chip label="Acknowledged" variant="status" size="small" color="info" sx={{ ml: 1, mb: 0.3 }} />;
    case DisplayStatus.IN_PROGRESS:
      return <Chip label="In progress" variant="status" size="small" color="error" sx={{ ml: 1, mb: 0.3 }} />;
    default:
      return null;
  }
};

export const getPriorityBadge = (priority: InsightPriority | null): React.ReactNode => {
  switch (priority) {
    case InsightPriority.High:
      return <Chip label="High priority" variant="status" size="small" color="error" sx={{ ml: 1, mb: 0.3 }} />;
    case InsightPriority.Medium:
      return <Chip label="Medium priority" variant="status" size="small" color="info" sx={{ ml: 1, mb: 0.3 }} />;
    case InsightPriority.Low:
      return <Chip label="Low priority" variant="status" size="small" sx={{ ml: 1, mb: 0.3 }} />;
    default:
      return null;
  }
};

const getIsInternalBadge = (isInternal: boolean): React.ReactNode => {
  if (isInternal) {
    return <Chip label="DoiT internal" color="primary" variant="status" sx={{ ml: 1, mb: 0.3 }} />;
  }
  return null;
};

const getEasyWinBadge = (easyWinDescription: string): React.ReactNode => {
  if (easyWinDescription) {
    return <EasyWinChip tooltipText={easyWinDescription} />;
  }
  return null;
};

type InsightBadgesProps = {
  insight: Insight;
};

const InsightBadges: React.FC<InsightBadgesProps> = ({ insight }) => {
  const badges: React.ReactNode[] = [
    getStatusBadge(insight),
    getPriorityBadge(insight.priority),
    getIsInternalBadge(insight.isInternal),
    getEasyWinBadge(insight.easyWinDescription),
  ].filter(Boolean);

  if (badges.length === 0) {
    return null;
  }

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ color: "text.secondary", mt: 0.5, mb: 0.5 }}
      height="28px"
    >
      {badges.map((badge, index) => (
        <Stack key={`${insight.key}-${index}`} direction="row" spacing={0.75} alignItems="center">
          {badge}
        </Stack>
      ))}
    </Stack>
  );
};

export default InsightBadges;
